import { useState } from 'react';
import { Plus, Trash2, GripVertical } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import type { Block, PricingBlockContent, PricingLineItem } from '@/types';
import { formatCurrency } from '@/lib/format';

interface PricingBlockProps {
  block: Block;
  isEditable?: boolean;
  onUpdate?: (patch: Partial<Block>) => void;
}

const generateId = () => Math.random().toString(36).substring(2, 9);

function PricingBlock({ block, isEditable = false, onUpdate }: PricingBlockProps) {
  const content = block.content as PricingBlockContent;
  const [editingItem, setEditingItem] = useState<string | null>(null);

  const lineItems = content.lineItems || [];
  const currency = content.currency || 'EUR';
  const taxRate = content.taxRate || 21;

  const calculateTotals = (items: PricingLineItem[], rate: number) => {
    const subtotal = items.reduce((sum, item) => sum + item.total, 0);
    const tax = subtotal * (rate / 100);
    const discount = content.discount || 0;
    const total = subtotal + tax - discount;
    return { subtotal, tax, total };
  };

  const updateLineItem = (id: string, updates: Partial<PricingLineItem>) => {
    const updatedItems = lineItems.map((item) => {
      if (item.id !== id) return item;
      const updated = { ...item, ...updates };
      updated.total = updated.quantity * updated.unitPrice;
      return updated;
    });

    const { subtotal, tax, total } = calculateTotals(updatedItems, taxRate);
    onUpdate?.({
      content: {
        ...content,
        lineItems: updatedItems,
        subtotal,
        tax,
        total,
      },
    });
  };

  const addLineItem = () => {
    const newItem: PricingLineItem = {
      id: generateId(),
      description: 'Nieuw item',
      quantity: 1,
      unitPrice: 0,
      total: 0,
    };
    const updatedItems = [...lineItems, newItem];
    const { subtotal, tax, total } = calculateTotals(updatedItems, taxRate);
    onUpdate?.({
      content: {
        ...content,
        lineItems: updatedItems,
        subtotal,
        tax,
        total,
      },
    });
    setEditingItem(newItem.id);
  };

  const removeLineItem = (id: string) => {
    const updatedItems = lineItems.filter((item) => item.id !== id);
    const { subtotal, tax, total } = calculateTotals(updatedItems, taxRate);
    onUpdate?.({
      content: {
        ...content,
        lineItems: updatedItems,
        subtotal,
        tax,
        total,
      },
    });
  };

  const updateTaxRate = (rate: number) => {
    const { subtotal, tax, total } = calculateTotals(lineItems, rate);
    onUpdate?.({
      content: {
        ...content,
        taxRate: rate,
        subtotal,
        tax,
        total,
      },
    });
  };

  const updateDiscount = (discount: number) => {
    const subtotal = lineItems.reduce((sum, item) => sum + item.total, 0);
    const tax = subtotal * (taxRate / 100);
    const total = subtotal + tax - discount;
    onUpdate?.({
      content: {
        ...content,
        discount,
        subtotal,
        tax,
        total,
      },
    });
  };

  const formatAmount = (amount: number) => formatCurrency(amount, currency);

  if (isEditable) {
    return (
      <div className="rounded-lg border overflow-hidden">
        {/* Header */}
        <div className="p-4 bg-gray-100 border-b">
          <h6 className="text-base font-semibold">Prijsoverzicht</h6>
        </div>

        {/* Line items table */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50">
                <th className="w-10 p-2"></th>
                <th className="p-2 text-left font-medium">Omschrijving</th>
                <th className="w-[100px] p-2 text-right font-medium">Aantal</th>
                <th className="w-[120px] p-2 text-right font-medium">Prijs</th>
                <th className="w-[120px] p-2 text-right font-medium">Totaal</th>
                <th className="w-[50px] p-2"></th>
              </tr>
            </thead>
            <tbody>
              {lineItems.map((item) => (
                <tr key={item.id} className="border-b hover:bg-gray-50/50">
                  <td className="p-2">
                    <GripVertical className="h-4 w-4 text-gray-400 cursor-grab" />
                  </td>
                  <td className="p-2">
                    {editingItem === item.id ? (
                      <Input
                        value={item.description}
                        onChange={(e) => updateLineItem(item.id, { description: e.target.value })}
                        onBlur={() => setEditingItem(null)}
                        autoFocus
                        className="h-8"
                      />
                    ) : (
                      <span
                        onClick={() => setEditingItem(item.id)}
                        className="cursor-pointer text-sm hover:text-primary"
                      >
                        {item.description}
                      </span>
                    )}
                  </td>
                  <td className="p-2 text-right">
                    <input
                      type="number"
                      value={item.quantity}
                      onChange={(e) => updateLineItem(item.id, { quantity: Number(e.target.value) || 0 })}
                      min={0}
                      className="w-20 h-8 rounded-md border border-input bg-transparent px-2 py-1 text-sm text-right"
                    />
                  </td>
                  <td className="p-2 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <span className="text-sm text-muted-foreground">&euro;</span>
                      <input
                        type="number"
                        value={item.unitPrice}
                        onChange={(e) => updateLineItem(item.id, { unitPrice: Number(e.target.value) || 0 })}
                        min={0}
                        step={0.01}
                        className="w-24 h-8 rounded-md border border-input bg-transparent px-2 py-1 text-sm text-right"
                      />
                    </div>
                  </td>
                  <td className="p-2 text-right">
                    <span className="text-sm font-medium">{formatAmount(item.total)}</span>
                  </td>
                  <td className="p-2">
                    <Button variant="ghost" size="icon-sm" className="text-destructive" onClick={() => removeLineItem(item.id)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Add item button */}
        <div className="p-2 border-t">
          <Button variant="ghost" size="sm" onClick={addLineItem}>
            <Plus className="h-4 w-4 mr-1" />
            Item toevoegen
          </Button>
        </div>

        {/* Totals section */}
        <div className="p-4 bg-gray-50 border-t">
          <div className="flex justify-end gap-8">
            <div className="w-[300px]">
              {/* Subtotal */}
              <div className="flex justify-between mb-2">
                <span className="text-sm">Subtotaal:</span>
                <span className="text-sm">{formatAmount(content.subtotal || 0)}</span>
              </div>

              {/* Tax */}
              <div className="flex justify-between mb-2 items-center">
                <div className="flex items-center gap-2">
                  <span className="text-sm">BTW:</span>
                  <Select
                    value={String(taxRate)}
                    onValueChange={(value) => updateTaxRate(Number(value))}
                  >
                    <SelectTrigger className="w-[80px] h-7">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">0%</SelectItem>
                      <SelectItem value="9">9%</SelectItem>
                      <SelectItem value="21">21%</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <span className="text-sm">{formatAmount(content.tax || 0)}</span>
              </div>

              {/* Discount */}
              <div className="flex justify-between mb-2 items-center">
                <span className="text-sm text-green-600">Korting:</span>
                <div className="flex items-center gap-1">
                  <span className="text-sm text-muted-foreground">-&euro;</span>
                  <input
                    type="number"
                    value={content.discount || 0}
                    onChange={(e) => updateDiscount(Number(e.target.value) || 0)}
                    min={0}
                    step={0.01}
                    className="w-24 h-7 rounded-md border border-input bg-transparent px-2 py-1 text-sm text-right"
                  />
                </div>
              </div>

              <Separator className="my-2" />

              {/* Total */}
              <div className="flex justify-between">
                <span className="text-base font-bold">Totaal:</span>
                <span className="text-base font-bold text-primary">
                  {formatAmount(content.total || 0)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Read-only view
  return (
    <div className="rounded-lg border overflow-hidden">
      <div className="p-4 bg-gray-100">
        <h6 className="text-base font-semibold">Prijsoverzicht</h6>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50">
              <th className="p-2 text-left font-medium">Omschrijving</th>
              <th className="p-2 text-right font-medium">Aantal</th>
              <th className="p-2 text-right font-medium">Prijs</th>
              <th className="p-2 text-right font-medium">Totaal</th>
            </tr>
          </thead>
          <tbody>
            {lineItems.map((item) => (
              <tr key={item.id} className="border-b">
                <td className="p-2">{item.description}</td>
                <td className="p-2 text-right">{item.quantity}</td>
                <td className="p-2 text-right">{formatAmount(item.unitPrice)}</td>
                <td className="p-2 text-right">{formatAmount(item.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="p-4 bg-gray-50">
        <div className="flex justify-end">
          <div className="w-[250px]">
            <div className="flex justify-between mb-1">
              <span className="text-sm">Subtotaal:</span>
              <span className="text-sm">{formatAmount(content.subtotal || 0)}</span>
            </div>
            {(content.tax ?? 0) > 0 && (
              <div className="flex justify-between mb-1">
                <span className="text-sm">BTW ({taxRate}%):</span>
                <span className="text-sm">{formatAmount(content.tax || 0)}</span>
              </div>
            )}
            {(content.discount ?? 0) > 0 && (
              <div className="flex justify-between mb-1">
                <span className="text-sm text-green-600">Korting:</span>
                <span className="text-sm text-green-600">-{formatAmount(content.discount || 0)}</span>
              </div>
            )}
            <Separator className="my-2" />
            <div className="flex justify-between">
              <span className="text-base font-bold">Totaal:</span>
              <span className="text-base font-bold">{formatAmount(content.total || 0)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PricingBlock;
